import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./InviteUser.css";
import { Alert, Snackbar } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import LinkIcon from "@mui/icons-material/Link";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../context/BrandContext";
import { inviteClubMember, inviteClubAdmin } from "../funtionsBD/Mainservice";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import { getClubJoinPublicUrl } from "../../utils/clubJoinUrl";
import defaultAppLogo from "../../assets/smarttt-default-logo.png";
import InviteJoinQr from "./InviteJoinQr";
import { userHasAdminRole, userHasModeratorRole } from "../../utils/homeRole";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const METHODS = [
  { id: "link", labelKey: "inviteMethodLink", fallback: "Link", Icon: LinkIcon },
  { id: "email", labelKey: "inviteMethodEmail", fallback: "Email", Icon: MailOutlineIcon },
  { id: "qr", labelKey: "inviteMethodQr", fallback: "QR code", Icon: QrCode2Icon },
];

const ROLES = [
  { id: "player", labelKey: "inviteRolePlayer", fallback: "Player" },
  { id: "parent", labelKey: "inviteRoleParent", fallback: "Parent" },
  { id: "guest", labelKey: "inviteRoleGuest", fallback: "Guest" },
  { id: "admin", labelKey: "inviteRoleAdmin", fallback: "Admin", staffOnly: true },
  { id: "committee", labelKey: "inviteRoleCommittee", fallback: "Committee", staffOnly: true },
];

function readUserClubJoinHints() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const details = user?.club_details || user?.club || {};
    return {
      club_id: user?.club_id || details.club_id || null,
      club_code: details.club_code || user?.club_code || null,
      club_subdomain: details.club_subdomain || null,
    };
  } catch {
    return { club_id: null, club_code: null, club_subdomain: null };
  }
}

function roleHint(t, inviteRole) {
  if (inviteRole === "committee") {
    return (
      t("inviteCommitteeEmailHint") ||
      "They register as a committee member with this club already selected. They can view the dashboard and invite players, but cannot edit club setup."
    );
  }
  if (inviteRole === "admin") {
    return (
      t("inviteAdminEmailHint") ||
      "They register as club administrator with this club already selected."
    );
  }
  if (inviteRole === "guest") {
    return (
      t("inviteGuestEmailHint") ||
      "Competition players from another club can train here as guests."
    );
  }
  if (inviteRole === "parent") {
    return (
      t("inviteParentEmailHint") ||
      "Parents register their children with your club preselected."
    );
  }
  return (
    t("inviteEmailHint") ||
    "Email is required. Name is optional. Their personal link confirms the address."
  );
}

const InviteUser = () => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const userClub = readUserClubJoinHints();
  const clubId = userClub.club_id || brand?.club_id || null;
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }
  const canInviteStaff =
    userHasAdminRole(storedUser) || userHasModeratorRole(storedUser);
  const visibleRoles = ROLES.filter((role) => !role.staffOnly || canInviteStaff);

  const [method, setMethod] = useState("link");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [inviteRole, setInviteRole] = useState("player");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const joinUrl = useMemo(
    () =>
      getClubJoinPublicUrl(brand, {
        club_id: clubId,
        club_code: brand?.club_code || userClub.club_code,
        club_subdomain: brand?.club_subdomain || userClub.club_subdomain,
      }),
    [brand, clubId, userClub.club_code, userClub.club_subdomain]
  );

  const logoSrc = resolveClubImageUrl(brand?.logo_url) || defaultAppLogo;
  const clubName = brand?.brand_name || t("club") || "Club";
  const isValidEmail = EMAIL_RE.test(email.trim());

  const copyLink = useCallback(async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t("inviteCopyFailed") || "Could not copy the link");
    }
  }, [joinUrl, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail) {
      setError(t("please_enter_valid_email") || "Please enter a valid email");
      return;
    }
    setSending(true);
    setError("");
    try {
      const payload = {
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        club_id: clubId,
      };
      const response =
        inviteRole === "admin" || inviteRole === "committee"
          ? await inviteClubAdmin({ ...payload, invite_role: inviteRole })
          : await inviteClubMember({
              ...payload,
              as_guest: inviteRole === "guest",
              as_parent: inviteRole === "parent",
              invite_role: inviteRole,
            });
      const failed = response?.data?.failed;
      if (
        response.status === "error" ||
        (Array.isArray(failed) && failed.length && !response?.data?.sent?.length)
      ) {
        const reason = failed?.[0]?.reason;
        setError(
          reason
            ? `${response.message || t("error_sending_invitation")}: ${reason}`
            : response.message || t("error_sending_invitation")
        );
      } else {
        const sentEmail = response?.data?.sent?.[0]?.email || email.trim();
        const baseMsg =
          response.message ||
          (inviteRole === "admin"
            ? t("inviteAdminSent")
            : inviteRole === "committee"
              ? t("inviteCommitteeSent") || "Committee invitation sent"
            : inviteRole === "guest"
              ? t("inviteGuestSent")
              : inviteRole === "parent"
                ? t("inviteParentSent") || "Parent invitation sent"
                : t("inviteSent")) ||
          "Invitation sent";
        setSuccess(sentEmail ? `${baseMsg} → ${sentEmail}` : baseMsg);
        setOpen(true);
        setEmail("");
        setFirstName("");
        setLastName("");
      }
    } catch (err) {
      setError(err.message || t("error_sending_invitation") || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setCopied(false);
  }, [joinUrl]);

  useEffect(() => {
    setError("");
  }, [method]);

  return (
    <main className="invite-shell">
      <div className="invite-shell__glow" aria-hidden />

      <header className="invite-hero" data-tour-id="admin-invite-page">
        <div className="invite-hero__brand">
          <img src={logoSrc} alt="" className="invite-hero__logo" />
          <div className="invite-hero__copy">
            <p className="invite-hero__eyebrow">{clubName}</p>
            <h1 className="invite-hero__title">{t("invite") || "Invite"}</h1>
            <p className="invite-hero__sub">
              {t("invitePageSubtitle")}
            </p>
          </div>
        </div>
      </header>

      <div
        className="invite-methods"
        role="tablist"
        aria-label={t("inviteMethodLabel") || "Invitation method"}
        data-tour-id="admin-invite-methods"
      >
        {METHODS.map(({ id, labelKey, fallback, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={method === id}
            className={`invite-methods__tab ${method === id ? "is-active" : ""}`}
            data-tour-id={`admin-invite-method-${id}`}
            onClick={() => setMethod(id)}
          >
            <Icon fontSize="small" aria-hidden />
            <span>{t(labelKey) || fallback}</span>
          </button>
        ))}
      </div>

      {method === "link" ? (
        <section className="invite-panel invite-panel--primary" aria-labelledby="invite-share-heading">
          <div className="invite-panel__head">
            <span className="invite-panel__icon" aria-hidden>
              <LinkIcon fontSize="small" />
            </span>
            <div>
              <h2 id="invite-share-heading" className="invite-panel__title">
                {t("inviteShareTitle") || "Club registration link"}
              </h2>
              <p className="invite-panel__hint">
                {t("inviteShareHint") ||
                  "Anyone with this link chooses player or parent, then finishes signup with Pingflo."}
              </p>
            </div>
          </div>

          {joinUrl ? (
            <div className="invite-share">
              <p className="invite-share__url" title={joinUrl}>
                {joinUrl}
              </p>
              <button
                type="button"
                className={`invite-btn invite-btn--ghost ${copied ? "is-copied" : ""}`}
                onClick={copyLink}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                <span>{copied ? t("copied") || "Copied" : t("copyLink") || "Copy link"}</span>
              </button>
            </div>
          ) : (
            <p className="invite-error" role="status">
              {t("inviteJoinUrlMissing") || "Club join link is not available yet."}
            </p>
          )}
        </section>
      ) : null}

      {method === "email" ? (
        <section className="invite-panel invite-panel--primary" aria-labelledby="invite-email-heading">
          <div className="invite-panel__head">
            <span className="invite-panel__icon" aria-hidden>
              <MailOutlineIcon fontSize="small" />
            </span>
            <div>
              <h2 id="invite-email-heading" className="invite-panel__title">
                {t("inviteEmailTitle") || "Invite by email"}
              </h2>
              <p className="invite-panel__hint">{roleHint(t, inviteRole)}</p>
            </div>
          </div>

          <div
            className="invite-roles"
            role="tablist"
            aria-label={t("inviteRole") || "Invitation type"}
          >
            {visibleRoles.map((role) => (
              <button
                key={role.id}
                type="button"
                role="tab"
                aria-selected={inviteRole === role.id}
                className={`invite-roles__chip ${inviteRole === role.id ? "is-active" : ""}`}
                onClick={() => setInviteRole(role.id)}
              >
                {t(role.labelKey) || role.fallback}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="invite-form" noValidate>
            <label className="invite-field">
              <span>{t("mail")}</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                enterKeyHint="send"
                placeholder="name@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <div className="invite-fields-row">
              <label className="invite-field">
                <span>{t("firstname") || "First name"}</span>
                <input
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("optional") || "Optional"}
                />
              </label>
              <label className="invite-field">
                <span>{t("lastname") || "Last name"}</span>
                <input
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t("optional") || "Optional"}
                />
              </label>
            </div>

            {error ? (
              <p className="invite-error" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              className="invite-btn invite-btn--primary"
              disabled={!isValidEmail || sending}
            >
              <SendIcon fontSize="small" />
              <span>{sending ? t("sending") || "Sending…" : t("sendinvite") || "Send invite"}</span>
            </button>
          </form>
        </section>
      ) : null}

      {method === "qr" ? (
        <section className="invite-panel invite-panel--primary" aria-labelledby="invite-qr-heading">
          <div className="invite-panel__head">
            <span className="invite-panel__icon" aria-hidden>
              <QrCode2Icon fontSize="small" />
            </span>
            <div>
              <h2 id="invite-qr-heading" className="invite-panel__title">
                {t("inviteQrTitle") || "Scan to join the club"}
              </h2>
              <p className="invite-panel__hint">
                {t("inviteQrHint") ||
                  "Display or print this QR. Scanners choose player or parent, then Pingflo guides signup."}
              </p>
            </div>
          </div>

          <InviteJoinQr
            joinUrl={joinUrl}
            clubName={clubName}
            logoSrc={logoSrc}
            copied={copied}
            onCopy={copyLink}
            t={t}
          />
        </section>
      ) : null}

      {method !== "email" && error ? (
        <p className="invite-error" role="alert" style={{ marginTop: "0.75rem" }}>
          {error}
        </p>
      ) : null}

      <Snackbar
        open={open}
        autoHideDuration={3200}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <Alert variant="filled" severity="success" onClose={() => setOpen(false)}>
          {success}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default InviteUser;
